import React, { useState, useEffect, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getMandantes } from '../../services/Mandantes';
import { getConfigCarpetaMandante, saveConfigCarpetaMandante } from '../../services/CarpetaDocMandante';
import ListaConfigCarpeta from './ListaConfigCarpeta';

function siglaDesde(razonSocial = '') {
    return razonSocial.split(' ').slice(0, 2).map((w) => w[0] ?? '').join('').toUpperCase() || '—';
}

const GestionCarpetas = () => {
    const [dataMandante, setDataMandante] = useState([]);
    const [idMandante, setIdMandante] = useState('');

    const [docs, setDocs] = useState([]);
    const [baseDocs, setBaseDocs] = useState([]);

    const [searchTerm, setSearchTerm] = useState('');
    const [openGroups, setOpenGroups] = useState({ Logística: true, Obra: true, Anexo: true });
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const misMandantes = await getMandantes();
            setDataMandante(misMandantes);
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (!idMandante) {
            setDocs([]);
            setBaseDocs([]);
            return;
        }
        const fetchConfig = async () => {
            const data = await getConfigCarpetaMandante(idMandante);
            const d = data || [];
            setDocs(d);
            setBaseDocs(d.map((x) => ({ ...x })));
        };
        fetchConfig();
    }, [idMandante]);

    const mandante = useMemo(
        () => dataMandante.find((m) => m._id === idMandante) || null,
        [dataMandante, idMandante]
    );

    const cambios = useMemo(() => {
        const baseMap = new Map(baseDocs.map((d) => [d._id, d]));
        let n = 0;
        for (const doc of docs) {
            const base = baseMap.get(doc._id);
            if (!base) continue;
            if (doc.selected !== base.selected) { n++; continue; }
            if (doc.selected && (doc.vigenciaPersonalizada ?? null) !== (base.vigenciaPersonalizada ?? null)) n++;
        }
        return n;
    }, [docs, baseDocs]);

    const resumen = useMemo(() => {
        const logistica = docs.filter((d) => (d.tipo || 'Logística') === 'Logística');
        const obra      = docs.filter((d) => d.tipo === 'Obra');
        const anexo     = docs.filter((d) => d.tipo === 'Anexo');
        return {
            exigidos: docs.filter((d) => d.selected).length,
            total: docs.length,
            conVigencia: docs.filter((d) => d.selected && d.vigenciaPersonalizada != null).length,
            logisticaTotal: logistica.length,
            logisticaSel: logistica.filter((d) => d.selected).length,
            obraTotal: obra.length,
            obraSel: obra.filter((d) => d.selected).length,
            anexoTotal: anexo.length,
            anexoSel: anexo.filter((d) => d.selected).length,
        };
    }, [docs]);

    const handleToggle = (id) => {
        setDocs((prev) => prev.map((d) => (d._id === id ? { ...d, selected: !d.selected } : d)));
    };

    const handleVigencia = (id, value) => {
        const parsed = value === '' ? null : parseInt(value, 10);
        setDocs((prev) =>
            prev.map((d) => (d._id === id ? { ...d, vigenciaPersonalizada: isNaN(parsed) ? null : parsed } : d))
        );
    };

    const handleToggleGroup = (grupo) => {
        setOpenGroups((prev) => ({ ...prev, [grupo]: !prev[grupo] }));
    };

    const handleDescartar = () => {
        setDocs(baseDocs.map((x) => ({ ...x })));
    };

    const handleSeleccionarTodos = () => {
        setDocs((prev) => prev.map((d) => ({ ...d, selected: true })));
    };

    const handleDeseleccionarTodos = () => {
        setDocs((prev) => prev.map((d) => ({ ...d, selected: false })));
    };

    const handleGuardar = async () => {
        if (!idMandante) return;
        setGuardando(true);
        try {
            const seleccionados = docs
                .filter((d) => d.selected)
                .map((d) => ({ idDocuGeneral: d._id, vigenciaPersonalizada: d.vigenciaPersonalizada ?? null }));
            await saveConfigCarpetaMandante(idMandante, seleccionados);
            setBaseDocs(docs.map((x) => ({ ...x })));
            toast.success('Configuración guardada.');
        } catch {
            toast.error('Error al guardar la configuración.');
        } finally {
            setGuardando(false);
        }
    };

    const filtered = docs.filter((d) =>
        d.documentoGeneral.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const docsLogistica = filtered.filter((d) => (d.tipo || 'Logística') === 'Logística');
    const docsObra      = filtered.filter((d) => d.tipo === 'Obra');
    const docsAnexo     = filtered.filter((d) => d.tipo === 'Anexo');

    const sigla = mandante ? siglaDesde(mandante.razonSocial) : '—';

    return (
        <div className="m-2 md:m-10 mt-16 p-2 md:p-4 bg-gray-100 rounded-3xl">
            <div className="container mx-auto p-2">

                {/* Breadcrumb */}
                <nav
                    className="flex items-center gap-2 mb-5"
                    style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8d97aa' }}
                >
                    Gestión de carpetas
                    {mandante && (
                        <>
                            <span aria-hidden="true">/</span>
                            <b style={{ color: '#5c6880', fontWeight: 500 }}>{sigla}</b>
                        </>
                    )}
                </nav>

                {/* Pestañas */}
                <div className="flex items-end gap-1 pl-11 -mb-px relative z-10 overflow-x-auto">
                    <div
                        className="flex items-center gap-2 flex-shrink-0 bg-white text-blue-700 border border-gray-200 border-b-0 rounded-t-lg"
                        style={{ fontSize: 13, fontWeight: 500, padding: '9px 16px 13px', boxShadow: 'inset 0 -2px 0 0 #f2b01b' }}
                    >
                        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M4 4h12v12H4V4zm4 4h4M8 12h4" strokeLinecap="round" />
                        </svg>
                        Documentos exigibles
                        {idMandante && (
                            <span className="font-mono text-[11px] bg-blue-50 text-blue-700 rounded-full px-2">
                                {resumen.exigidos}
                            </span>
                        )}
                    </div>
                </div>

                {/* Carpeta */}
                <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden shadow-lg">

                    {/* Lomo */}
                    <div
                        className="flex-shrink-0 flex flex-col items-center justify-between py-4"
                        style={{ width: 44, background: '#17347f' }}
                    >
                        <span
                            className="rounded-full flex-shrink-0"
                            style={{ width: 10, height: 10, background: '#0c1f57' }}
                        />
                        <span
                            className="font-mono whitespace-nowrap"
                            style={{
                                writingMode: 'vertical-rl',
                                transform: 'rotate(180deg)',
                                fontSize: 11,
                                letterSpacing: '0.22em',
                                textTransform: 'uppercase',
                                color: '#a8bdea',
                            }}
                        >
                            <strong style={{ color: '#fff', fontWeight: 500 }}>{sigla}</strong>
                            {' · carpeta'}
                        </span>
                        <span
                            className="rounded-full flex-shrink-0"
                            style={{ width: 10, height: 10, background: '#0c1f57' }}
                        />
                    </div>

                    {/* Contenido */}
                    <div className="flex-1 min-w-0 flex flex-col">

                        {/* Portada */}
                        <header className="p-6 border-b border-gray-100" style={{ background: '#fafbfc' }}>
                            <div className="flex items-start gap-4 flex-wrap">
                                {/* Sello */}
                                <div
                                    className="w-12 h-12 flex-shrink-0 rounded-lg flex items-center justify-center font-mono text-[13px] font-medium"
                                    style={{ background: '#eef3fd', border: '1px solid #d6e2f9', color: '#17347f' }}
                                >
                                    {sigla}
                                </div>

                                <div className="flex-1 min-w-0">
                                    {idMandante ? (
                                        <>
                                            <h1 className="m-0 text-lg font-semibold leading-tight">
                                                {mandante?.razonSocial}
                                            </h1>
                                            <p className="m-0 mt-1 text-[12px] text-gray-400">
                                                Configuración de documentos exigibles
                                            </p>
                                        </>
                                    ) : (
                                        <h1 className="m-0 text-lg font-semibold text-gray-400">
                                            Sin mandante seleccionado
                                        </h1>
                                    )}
                                </div>

                                <select
                                    value={idMandante}
                                    onChange={(e) => setIdMandante(e.target.value)}
                                    className="text-[11px] border border-gray-300 p-1.5 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400 flex-shrink-0"
                                >
                                    <option value="">— Cambiar mandante —</option>
                                    {dataMandante.map((m) => (
                                        <option key={m._id} value={m._id}>{m.razonSocial}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Indicadores */}
                            {idMandante && (
                                <dl
                                    className="mt-4 grid gap-2"
                                    style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}
                                >
                                    {[
                                        { label: 'Docs exigibles', val: resumen.exigidos, sub: `/ ${resumen.total}` },
                                        { label: 'Vigencia propia', val: resumen.conVigencia },
                                        { label: 'Logística', val: resumen.logisticaSel, sub: `/ ${resumen.logisticaTotal}` },
                                        { label: 'Obra', val: resumen.obraSel, sub: `/ ${resumen.obraTotal}` },
                                        { label: 'Anexo', val: resumen.anexoSel, sub: `/ ${resumen.anexoTotal}` },
                                    ].map(({ label, val, sub }) => (
                                        <div key={label} className="border border-gray-100 rounded-lg p-3">
                                            <dt className="text-[11px] text-gray-400 m-0">{label}</dt>
                                            <dd className="font-mono text-xl font-medium mt-1 m-0">
                                                {val}
                                                {sub && (
                                                    <small className="text-[12px] text-gray-400 font-normal"> {sub}</small>
                                                )}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            )}
                        </header>

                        {/* Body */}
                        {idMandante ? (
                            <>
                                <ListaConfigCarpeta
                                    docsLogistica={docsLogistica}
                                    docsObra={docsObra}
                                    docsAnexo={docsAnexo}
                                    allDocs={docs}
                                    openGroups={openGroups}
                                    searchTerm={searchTerm}
                                    onSearchChange={setSearchTerm}
                                    onToggle={handleToggle}
                                    onVigencia={handleVigencia}
                                    onToggleGroup={handleToggleGroup}
                                />

                                {/* Barra de guardado */}
                                <div
                                    className="flex items-center gap-3 px-6 py-3 border-t border-gray-100 sticky bottom-0"
                                    style={{ background: '#fafbfc' }}
                                >
                                    <div
                                        className="flex flex-1 items-center gap-2"
                                        style={{ fontSize: 13, color: '#5c6880' }}
                                    >
                                        <span
                                            className="flex-shrink-0 rounded-full"
                                            style={{
                                                width: 7,
                                                height: 7,
                                                background: cambios > 0 ? '#f2b01b' : '#17a06f',
                                            }}
                                        />
                                        {cambios > 0
                                            ? `${cambios} cambio${cambios === 1 ? '' : 's'} sin guardar`
                                            : 'Sin cambios pendientes'}
                                    </div>
                                    <button
                                        onClick={handleSeleccionarTodos}
                                        disabled={!idMandante}
                                        className="text-[13px] font-medium px-3 py-1.5 rounded border-0 bg-transparent text-blue-600 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Seleccionar todos
                                    </button>
                                    <button
                                        onClick={handleDeseleccionarTodos}
                                        disabled={!idMandante}
                                        className="text-[13px] font-medium px-3 py-1.5 rounded border-0 bg-transparent text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Deseleccionar todos
                                    </button>
                                    <button
                                        onClick={handleDescartar}
                                        disabled={cambios === 0}
                                        className="text-[13px] font-medium px-3 py-1.5 rounded border-0 bg-transparent text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                    >
                                        Descartar
                                    </button>
                                    <button
                                        onClick={handleGuardar}
                                        disabled={cambios === 0 || guardando}
                                        className="text-[13px] font-medium px-4 py-1.5 rounded text-white disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                                        style={{ background: '#17347f' }}
                                    >
                                        {guardando ? 'Guardando…' : 'Guardar configuración'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <span className="text-5xl mb-4" style={{ opacity: 0.2 }}>📂</span>
                                <p className="text-gray-400 text-sm">
                                    Seleccione un mandante para configurar sus documentos.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

            </div>
            <ToastContainer />
        </div>
    );
};

export default GestionCarpetas;
